import { defineComponent as _defineComponent } from 'vue'
import FileUpload from '@/components/FileUpload.vue';
    import THGCarForm from '@/components/thg/cars/THGCarForm.vue';
    import { computed, onMounted, ref, watch } from 'vue';
    import { THGCar } from '@/interfaces/thg/THGCar';
    import { THGVehicleClass } from '@/enums/thg/THGVehicleClass';
    import { useTHGGlobalStore } from '@/storenew/thg/global';
    import { Country } from '@/enums/Country';
    import { analyze, check } from '@/api/thg/vrc';
    import { toBase64 } from '@/utils/files';
    import emitter from '@/utils/mitt';
    import THGUserAutoComplete from '@/components/thg/user/THGUserAutoComplete.vue';
    import { THGUser } from '@/interfaces/thg/THGUser';
    import { THGCarStatus } from '@/enums/thg/THGCarStatus';
    import { alertError } from '@/utils/dialogs';
    import { VRCResponseResultStatus } from '@/enums/thg/THGVehicleRegistrationCertificate';
    import { addTHGCar } from '@/api/thg/cars';
    import { ToastProgrammatic as Toast } from 'buefy';
    import { FormMode } from '@/enums/misc';
    import { THGCarCreateResultEnum } from '@/enums/thg/THGCarCreateResultEnum';
    import { THGChargingPlugType } from '@/enums/thg/THGChargingPlugType';
    import { AustrianVRCType } from '@/enums/thg/THGAustrianVRCType';
    import { THGCarPayoffModel } from '@/enums/thg/THGCarPayoffModel';
    import { getPayoffCombiYears, getTHGPayoffAmount } from '@/utils/thg';
    import { storeToRefs } from 'pinia';
    import { until } from '@vueuse/core';
    import { THGTag } from '@/interfaces/thg/THGTag';

    
export default /*#__PURE__*/_defineComponent({
  __name: 'NewTHGCar',
  setup(__props) {

    const { currentCountry } = useTHGGlobalStore();
    const { payoffs } = storeToRefs(useTHGGlobalStore());
    const currentUser = ref<THGUser>();

    const currentAustrianVRCType = ref<AustrianVRCType>();

    if (currentCountry === Country.AT) {
        currentAustrianVRCType.value = AustrianVRCType.DEFAULT;
    }

    // add some default data to provide reactivity
    // @TODO: maybe remove when migrating to Vue 3?
    const defaultCarData: THGCar = {
        year: 2026,
        vin: '', // is always re-written when VRC changed
        status: THGCarStatus.DATA_RECEIVED,
        vehicle_class: THGVehicleClass.PV,
        fix_payoff_amount: undefined,
        flex_payoff_amount: undefined,
        combi_fix_payoff_amount: undefined,
        combi_express_payoff_amount: undefined,
        charging_point_address: undefined, // is always re-written when current user changed
        tags: [],
    };
    const newCar = ref<THGCar>({ ...defaultCarData });

    const frontFile = ref<File | undefined>(undefined);
    const backFile = ref<File | undefined>(undefined);

    const sendMail = ref<boolean>(false);
    const analyzeVRC = ref<boolean>(true);

    const frontFileLabel = computed<string>(() => {
        if (currentCountry === Country.AT) {
            return 'Zulassungsschein Vorderseite (Deckblatt)';
        }

        return 'Zulassungsschein Vorderseite';
    });

    const backFileLabel = computed<string>(() => {
        if (currentCountry === Country.AT) {
            return 'Zulassungsschein Rückseite (Daten)';
        }

        return 'Zulassungsschein Rückseite';
    });

    async function resetForm() {
        newCar.value = { ...defaultCarData };
        frontFile.value = undefined;
        backFile.value = undefined;
        await initPayoffs();

        if (currentCountry === Country.AT) {
            newCar.value.charging_plug_type = THGChargingPlugType.TYPE2;
            newCar.value.charging_point_address = {};
        }
    }

    watch(currentUser, () => {
        if (!currentUser.value) {
            throw new Error('Current user is undefined');
        }

        // copy the user's address from profile to charging point address
        newCar.value.charging_point_address = {
            zip_code: currentUser.value.zip_code || '',
            city: currentUser.value.city || '',
            street: currentUser.value.street || '',
        };
    });

    if (currentCountry === Country.AT) {
        watch(currentAustrianVRCType, (value) => {
            // disable analyzeVRC field if it's type card
            analyzeVRC.value = value !== AustrianVRCType.CARD;

            if (value === AustrianVRCType.DEFAULT) {
                // switch to default: reset card number
                newCar.value.card_number = undefined;
            }
        });
    }

    /**
     * Retrieve the VRC data by sending the card number and license plate to our api.
     */
    async function retrieveOESDData() {
        emitter.emit('showLoader');

        if (!newCar.value.card_number || !newCar.value.license_plate) {
            throw new Error('Card number or license plate is missing');
        }

        const resp = await analyze({
            year: newCar.value.year,
            oesd_payload: {
                cardNumber: newCar.value.card_number,
                licencePlateNumber: newCar.value.license_plate,
            },
        });

        if (!resp.parsedBody) {
            emitter.emit('hideLoader');
            alertError('Fehler', 'Daten der Scheckkarte können nicht abgerufen werden.');
            return;
        } else if (resp.parsedBody.result === VRCResponseResultStatus.OESD_DATA_NOT_FOUND) {
            emitter.emit('hideLoader');
            alertError(
                'Fehler',
                'Kombination aus Kartennummer und Kennzeichen kann nicht gefunden werden.',
            );
            return;
        } else if (resp.parsedBody.result !== VRCResponseResultStatus.ACCEPTED) {
            emitter.emit('hideLoader');
            alertError(
                'Fehler',
                `Folgender Fehler trat auf beim Abrufen der Daten: ${resp.parsedBody.result}`,
            );
            return;
        }

        // no error, continue
        const data = resp.parsedBody.data;

        // fill form with data
        newCar.value.vin = data.vin;
        newCar.value.drive_code = data.drive_code;
        newCar.value.registration_date = data.registration_date;
        newCar.value.initial_registration_date = data.initial_registration_date;
        newCar.value.make = data.make;
        newCar.value.national_code = data.national_code;
        newCar.value.commercial_name = data.commercial_name;

        emitter.emit('hideLoader');
    }

    watch(frontFile, async () => {
        if (frontFile.value) {
            emitter.emit('showLoader');

            // write the frontFile's content (always, AT and DE) including the mime type
            newCar.value.vrc_front_file = await toBase64(frontFile.value, false);

            // analyze the vrc if country is DE and analyzeVRC is true
            if (currentCountry === Country.DE && analyzeVRC.value) {
                const resp = await analyze({
                    // KÜS Data Service needs the image base64 string without mime type attached
                    image: await toBase64(frontFile.value, true),
                    year: newCar.value.year,
                });

                if (!resp.parsedBody) {
                    throw new Error('Error while retrieving vrc result.');
                }

                if (resp.parsedBody.result === VRCResponseResultStatus.INVALID) {
                    emitter.emit('hideLoader');
                    frontFile.value = undefined;
                    alertError('Fehler', 'Zulassungsschein kann nicht eingelesen werden.');
                    return;
                }

                const vrcData = resp.parsedBody.data;

                newCar.value.vin = vrcData.vin;
                newCar.value.registration_date = vrcData.registration_date;
                newCar.value.initial_registration_date = vrcData.initial_registration_date;
                newCar.value.error_checking_numbers = vrcData.error_checking_numbers;
                newCar.value.probabilities = vrcData.probabilities;

                if (vrcData.license_plate) {
                    newCar.value.license_plate = vrcData.license_plate;
                }

                if (vrcData.drive_code) {
                    newCar.value.drive_code = vrcData.drive_code;
                }

                if (vrcData.make) {
                    newCar.value.make = vrcData.make;
                }

                if (vrcData.hsn_tsn) {
                    newCar.value.hsn_tsn = vrcData.hsn_tsn;
                }

                if (vrcData.first_name) {
                    newCar.value.first_name = vrcData.first_name;
                }

                if (vrcData.name) {
                    newCar.value.name = vrcData.name;
                }

                if (vrcData.zip_code) {
                    newCar.value.zip_code = vrcData.zip_code;
                }

                if (vrcData.city) {
                    newCar.value.city = vrcData.city;
                }

                if (vrcData.street) {
                    newCar.value.street = vrcData.street;
                }
            }
            emitter.emit('hideLoader');
        }
    });

    watch(backFile, async () => {
        if (backFile.value) {
            emitter.emit('showLoader');

            // write the backFile's content
            const backFileBase64 = await toBase64(backFile.value, false);
            newCar.value.vrc_back_file = backFileBase64;

            // analyze the vrc analyzeVRC is true, for THG DE we don't need the backFile at all (it's disabled)
            if (analyzeVRC.value) {
                const resp = await analyze({
                    image: backFileBase64,
                    year: newCar.value.year,
                });

                if (!resp.parsedBody) {
                    throw new Error('Error while retrieving vrc result.');
                }

                if (resp.parsedBody.result !== VRCResponseResultStatus.ACCEPTED) {
                    emitter.emit('hideLoader');
                    backFile.value = undefined;
                    alertError(
                        'Fehler',
                        `Zulassungsschein kann nicht eingelesen werden. Fehlercode: ${resp.parsedBody.result.toUpperCase()}`,
                    );

                    return;
                }

                const vrcData = resp.parsedBody.data;

                newCar.value.vin = vrcData.vin;
                newCar.value.drive_code = vrcData.drive_code;
                newCar.value.registration_date = vrcData.registration_date;
                newCar.value.initial_registration_date = vrcData.initial_registration_date;

                if (vrcData.vehicle_class) {
                    newCar.value.vehicle_class = vrcData.vehicle_class;
                }

                if (vrcData.license_plate) {
                    newCar.value.license_plate = vrcData.license_plate;
                }

                if (vrcData.name) {
                    newCar.value.name = vrcData.name;
                }

                if (vrcData.make) {
                    newCar.value.make = vrcData.make;
                }

                if (vrcData.national_code) {
                    newCar.value.national_code = vrcData.national_code;
                }

                if (vrcData.commercial_name) {
                    newCar.value.commercial_name = vrcData.commercial_name;
                }
            }

            emitter.emit('hideLoader');
        }
    });

    async function checkVRC(car: THGCar, user: THGUser, year: number) {
        if (!car.registration_date) {
            throw new Error('Registration date is undefined');
        }

        const checkResp = await check({
            vin: car.vin,
            year: year,
            registration_date: car.registration_date,
            user_id: user.id,
        });

        if (!checkResp?.parsedBody) {
            throw new Error('Cannot check vrc');
        }

        const result = checkResp.parsedBody.result;

        if (result !== VRCResponseResultStatus.ACCEPTED) {
            emitter.emit('hideLoader');
            alertError(
                'Fehler',
                `Die Prüfung der eingegebenen Daten schlug fehl. Code: <b>${result.toUpperCase()}</b>`,
            );

            return;
        }
    }

    async function addCar() {
        emitter.emit('showLoader');

        if (!currentUser.value) {
            throw new Error('Current user is undefined.');
        }

        // check the uba_submitted_date this way because buefy's datepicker's required attribute is not working
        if (
            (newCar.value.status === THGCarStatus.UBA_SUBMITTED ||
                newCar.value.status === THGCarStatus.CERT_RECEIVED ||
                newCar.value.status === THGCarStatus.CERT_DECLINED ||
                newCar.value.status === THGCarStatus.INCOME_PAID_OUT) &&
            !newCar.value.uba_submitted_date
        ) {
            emitter.emit('hideLoader');
            alertError('Fehler', 'Einreichdatum muss bei diesem Status gesetzt sein.');
            return;
        }

        if (newCar.value.status === THGCarStatus.PRE_RESERVED && newCar.value.year !== 2027) {
            emitter.emit('hideLoader');
            alertError('Fehler', 'Vorreservierungen können nur für 2027 erstellt werden.');
            return;
        }

        // run VRC checks
        if (
            (newCar.value.payoff_model === THGCarPayoffModel.COMBI_FIX ||
                newCar.value.payoff_model === THGCarPayoffModel.COMBI_EXPRESS) &&
            newCar.value.combi_years
        ) {
            for (const year of newCar.value.combi_years) {
                await checkVRC(newCar.value, currentUser.value, year);
            }
        } else {
            await checkVRC(newCar.value, currentUser.value, newCar.value.year);
        }

        if (newCar.value.tags) {
            newCar.value.tags = newCar.value.tags.map((tag) => (tag as THGTag)._id as string);
        }

        // add user to newCar
        newCar.value.user_id = currentUser.value.id;
        const addResp = await addTHGCar(newCar.value, !sendMail.value);

        if (
            (newCar.value.payoff_model === THGCarPayoffModel.COMBI_FIX ||
                THGCarPayoffModel.COMBI_EXPRESS) &&
            newCar.value.combi_years
        ) {
            const payload = { ...newCar.value, year: newCar.value.year + 1 };

            if (currentCountry === Country.AT) {
                // Austrian case: second registration -> "Vorreserviert"
                payload.status = THGCarStatus.PRE_RESERVED;
            }

            const addSecondCombiResp = await addTHGCar(payload, !sendMail.value);

            if (!addSecondCombiResp.parsedBody) {
                emitter.emit('hideLoader');
                alertError(
                    'Fehler',
                    `Unbekannter Fehler beim Erstellen des zweiten Fahrzeugs für ${
                        newCar.value.year + 1
                    }`,
                );
            }
        }

        if (!addResp.parsedBody) {
            emitter.emit('hideLoader');
            alertError('Fehler', 'Unbekannter Fehler beim Erstellen des Fahrzeugs.');
            throw new Error('Could not create car.');
        }

        if (addResp.parsedBody.result === THGCarCreateResultEnum.REGISTRATION_LIMIT_EXCEEDED) {
            emitter.emit('hideLoader');
            alertError(
                'Fehler',
                `Das Registrierungslimit dieses Benutzers (${currentUser.value.registration_limit} Fahrzeuge) für ${newCar.value.year} ist bereits erreicht.`,
            );

            return;
        }

        await resetForm();
        emitter.emit('hideLoader');

        Toast.open({ type: 'is-success', message: 'Registrierung wurde erfolgreich angelegt.' });
    }

    /**
     * Initialize the 'new car' form with default data.
     */
    async function initPayoffs() {
        if (
            currentCountry === Country.AT ||
            (currentCountry === Country.DE && newCar.value.year > 2024)
        ) {
            if (!payoffs.value) {
                throw new Error('Payoffs are undefined');
            }

            const default_fix_payoff_amount = getTHGPayoffAmount(
                payoffs.value,
                THGCarPayoffModel.FIX,
                newCar.value.year,
            );

            if (default_fix_payoff_amount) {
                newCar.value.fix_payoff_amount = {
                    default: default_fix_payoff_amount,
                    first: getTHGPayoffAmount(
                        payoffs.value,
                        THGCarPayoffModel.FIX,
                        newCar.value.year,
                        'first',
                    ),
                };
            }

            const default_express_payoff_amount = getTHGPayoffAmount(
                payoffs.value,
                THGCarPayoffModel.EXPRESS,
                newCar.value.year,
            );

            if (default_express_payoff_amount) {
                newCar.value.express_payoff_amount = {
                    default: default_express_payoff_amount,
                };
            }

            newCar.value.flex_payoff_amount = {
                min: 0,
                max: 0,
            };

            const default_combi_fix_payoff_amount = getTHGPayoffAmount(
                payoffs.value,
                THGCarPayoffModel.COMBI_FIX,
                getPayoffCombiYears(payoffs.value, THGCarPayoffModel.COMBI_FIX, newCar.value.year),
            );

            if (default_combi_fix_payoff_amount) {
                newCar.value.combi_fix_payoff_amount = {
                    default: default_combi_fix_payoff_amount,
                    years: getPayoffCombiYears(
                        payoffs.value,
                        THGCarPayoffModel.COMBI_FIX,
                        newCar.value.year,
                    ),
                };
            }

            const default_combi_express_payoff_amount = getTHGPayoffAmount(
                payoffs.value,
                THGCarPayoffModel.COMBI_EXPRESS,
                getPayoffCombiYears(
                    payoffs.value,
                    THGCarPayoffModel.COMBI_EXPRESS,
                    newCar.value.year,
                ),
            );

            if (default_combi_express_payoff_amount) {
                newCar.value.combi_express_payoff_amount = {
                    default: default_combi_express_payoff_amount,
                    years: getPayoffCombiYears(
                        payoffs.value,
                        THGCarPayoffModel.COMBI_EXPRESS,
                        newCar.value.year,
                    ),
                };
            }
        }
    }

    // set sendMail flag to false if status not PRE_RESERVED or DATA_RECEIVED
    watch<THGCarStatus | undefined>(
        () => newCar.value.status,
        (newValue) => {
            if (newValue !== THGCarStatus.PRE_RESERVED && newValue !== THGCarStatus.DATA_RECEIVED) {
                sendMail.value = false;
            }
        },
    );

    watch([() => newCar.value.year, () => newCar.value.payoff_model], () => {
        initPayoffs();
    });

    onMounted(async () => {
        if (!payoffs.value) {
            await until(payoffs).toBeTruthy();
        }

        await initPayoffs();
        if (currentCountry === Country.AT) {
            newCar.value.charging_plug_type = THGChargingPlugType.TYPE2;
            newCar.value.charging_point_address = {};
        }
    });

return { __sfc: true,currentCountry, payoffs, currentUser, currentAustrianVRCType, defaultCarData, newCar, frontFile, backFile, sendMail, analyzeVRC, frontFileLabel, backFileLabel, resetForm, retrieveOESDData, checkVRC, addCar, initPayoffs, FileUpload, THGCarForm, Country, THGUserAutoComplete, THGCarStatus, FormMode, AustrianVRCType }
}

})